import type {
    AvmSpec,
    AvmSpecOutputModel,
    AvmSpecOutputModelParameter,
    AvmSpecOutputOnly,
    AvmSpecOutputParameter,
    AvmSpecParamCategory,
    RawRevaSpecGroup,
    RawRevaSpecOutputCategory,
    RawRevaSpecOutputRef,
    RawRevaSpecPage,
    RawRevaSpecRow,
    RevaSpecApiResponse,
    RevaSpecObjectInputRange,
    RevaValueParameterType,
} from "../reva-spec";
import type {
    RevaIndicationEndpoint,
    RevaSpec,
    RevaSpecApiParams,
    RevaSpecInputParameter,
    RevaSpecOutputCategory,
    RevaSpecOutputColumn,
    RevaSpecOutputPage,
    RevaSpecOutputParameter,
    RevaSpecOutputRef,
    RevaSpecOutputRow,
    RevaSpecOutputRowFormats,
    RevaSpecOutputRowGroup,
    RevaSpecParamCategory,
    RevaSpecSegmentInputs,
    SpecConversionConfig,
} from "./conversion-types";

export type {
    RevaIndicationEndpoint,
    RevaSpec,
    RevaSpecApiParams,
    RevaSpecApiParamsSegmentConfig,
    RevaSpecAvmSpecs,
    RevaSpecInputParameter,
    RevaSpecOutputCategory,
    RevaSpecOutputColumn,
    RevaSpecOutputPage,
    RevaSpecOutputParameter,
    RevaSpecOutputRef,
    RevaSpecOutputRow,
    RevaSpecOutputRowFormats,
    RevaSpecOutputRowGroup,
    RevaSpecParamCategory,
    RevaSpecSegmentInputs,
    SpecConversionConfig,
} from "./conversion-types";

/** static columns of every converted output page. */
const COLUMNS: RevaSpecOutputColumn[] = [
    {headerDe: "Anzahl / Ansatz / Wert", headerEn: "Count / Factor / Value", field: "factor"},
    {headerDe: "Wertansatz Marktwert", headerEn: "Market Value Factor", field: "market_value_factor"},
    {headerDe: "Marktwert", headerEn: "Market Value", field: "market_value"},
    {headerDe: "Wertansatz Beleihungswert", headerEn: "Lending Value Factor", field: "lending_value_factor"},
    {headerDe: "Beleihungswert", headerEn: "Lending Value", field: "lending_value"},
];

/** `displayFormat` per output parameter of an AVM spec, to format a cell's low/high companion values (#19096). */
type DisplayFormats = Record<string, string>;

function indexDisplayFormats(avmSpec: {outputParameters?: AvmSpecOutputParameter[]} | undefined): DisplayFormats {
    const result: DisplayFormats = {};
    for (const param of avmSpec?.outputParameters ?? []) {
        if (param?.parameter && param.displayFormat) result[param.parameter] = param.displayFormat;
    }
    return result;
}

/**
 * Convert a page from the raw API result to internal format.
 */
function convertPage(endpointSpec: RawRevaSpecPage | undefined, displayFormats: DisplayFormats): RevaSpecOutputPage {
    let rowGroups: RevaSpecOutputRowGroup[] = [];
    let rows: RevaSpecOutputRow[] = [];
    if (
        endpointSpec &&
        typeof endpointSpec === "object" &&
        Array.isArray(endpointSpec.groups) &&
        Array.isArray(endpointSpec.rows)
    ) {
        rowGroups = endpointSpec.groups.map((groupSpec) => convertGroup(groupSpec));
        rows = endpointSpec.rows.map((rowSpec) => convertRow(rowSpec, displayFormats));
    } else {
        console.warn("Found invalid response for spec page:", endpointSpec);
    }
    return {columns: COLUMNS, rowGroups, rows};
}

function convertGroup(groupSpec: RawRevaSpecGroup): RevaSpecOutputRowGroup {
    return {
        group: groupSpec.group,
        headerDe: groupSpec.titleDe,
        headerEn: groupSpec.titleEn,
        hideUnlessApiParametersExisting: groupSpec.hideUnlessApiParametersExisting ?? null,
        unfoldInitially: groupSpec.unfoldInitially,
    };
}

function convertRow(rowSpec: RawRevaSpecRow, displayFormats: DisplayFormats): RevaSpecOutputRow {
    return {
        group: rowSpec.group,
        titleDe: rowSpec.titleDe,
        titleEn: rowSpec.titleEn,
        formats: convertRowFormats(rowSpec.formats),
        factor: convertOutputRef(rowSpec.factor, displayFormats),
        market_value_factor: convertOutputRef(rowSpec.marketValueFactor, displayFormats),
        market_value: convertOutputRef(rowSpec.marketValue, displayFormats),
        lending_value_factor: convertOutputRef(rowSpec.lendingValueFactor, displayFormats),
        lending_value: convertOutputRef(rowSpec.lendingValue, displayFormats),
        hideUnlessApiParametersExisting: rowSpec.hideUnlessApiParametersExisting ?? null,
    };
}

function convertRowFormats(formatSpec: string[] | undefined): RevaSpecOutputRowFormats {
    if (!formatSpec || !Array.isArray(formatSpec)) return {};
    const formats: RevaSpecOutputRowFormats = {};
    for (const format of formatSpec) {
        switch (format.toUpperCase()) {
            case "BOLD":
                formats.bold = true;
                break;
            case "LINE_ABOVE":
                formats.lineAbove = true;
                break;
            case "BLANK_LINE_ABOVE":
            case "SPACE_ABOVE":
                formats.blankLineAbove = true;
                break;
            case "GREYED_OUT":
            case "GRAYED_OUT":
            case "GREY":
            case "GRAY":
                formats.grayedOut = true;
                break;
            default:
                console.warn(`Invalid format ${format} found in spec for output row`, formatSpec);
        }
    }
    return formats;
}

function convertOutputRef(
    outputSpec: RawRevaSpecOutputRef | undefined,
    displayFormats: DisplayFormats,
): RevaSpecOutputRef | null {
    if (!outputSpec || typeof outputSpec !== "object") {
        return null;
    }
    return {
        field: outputSpec.apiParam,
        field_is_input: outputSpec.input,
        format: outputSpec.displayFormat,
        unitDe: outputSpec.unitDe,
        unitEn: outputSpec.unitEn,
        descriptionDe: outputSpec.descriptionDe,
        descriptionEn: outputSpec.descriptionEn,
        hideIfEmpty: outputSpec.hideIfEmpty,
        valueIfEmpty: outputSpec.valueIfEmpty ?? null,
        associatedInputApiParam: outputSpec.associatedInputApiParam ?? null,
        categories: convertCategories(outputSpec.categories),
        lowApiParam: outputSpec.lowApiParam ?? null,
        highApiParam: outputSpec.highApiParam ?? null,
        lowFormat: outputSpec.lowApiParam ? displayFormats[outputSpec.lowApiParam] : undefined,
        highFormat: outputSpec.highApiParam ? displayFormats[outputSpec.highApiParam] : undefined,
    };
}

/** Convert categories of an output-ref cell (format=CATEGORY). */
function convertCategories(categoriesSpec: RawRevaSpecOutputCategory[] | undefined): RevaSpecOutputCategory[] | null {
    if (!Array.isArray(categoriesSpec)) {
        return null;
    }
    return categoriesSpec.map((categorySpec) => ({
        categoryNumber: categorySpec.categoryNumber,
        titleDe: categorySpec.nameDe,
        titleEn: categorySpec.nameEn,
    }));
}

/** Convert categories of an api-param (input/output parameter) definition. */
function convertParamCategories(categories: AvmSpecParamCategory[] | undefined): RevaSpecParamCategory[] | null {
    if (!Array.isArray(categories)) {
        return null;
    }
    return categories.map((category) => {
        const {categoryNumber, shortNameDe, shortNameEn, titleDe, titleEn, groupDe, groupEn} = category;
        return {categoryNumber, shortNameDe, shortNameEn, titleDe, titleEn, ...(groupDe && {groupDe, groupEn})};
    });
}

/**
 * Convert one AVM segment's input parameters (per sub-segment, e.g. per building type) to internal
 * format, tagging every parameter with the given `parameterType`.
 */
function convertSegmentInputs(
    specJson: AvmSpec | AvmSpecOutputOnly | undefined,
    parameterType: RevaValueParameterType,
): RevaSpecSegmentInputs {
    if (
        typeof specJson !== "object" ||
        specJson === null ||
        !("segments" in specJson) ||
        !Array.isArray(specJson.segments)
    ) {
        console.warn("Invalid spec?", specJson);
        return {};
    }
    const inputsBySegmentArr = specJson.segments.map((segment) => {
        let inputs = specJson.inputParameters?.[segment];
        if (!Array.isArray(inputs)) {
            inputs = [];
        }
        const inputMeta = inputs.map((input) => {
            const {
                parameter,
                required,
                type,
                minValue,
                maxValue,
                nameDe,
                nameEn,
                descriptionDe,
                descriptionEn,
                guiAdditionalHelpTextDe,
                guiAdditionalHelpTextEn,
                unitDe,
                unitEn,
                categories,
                displayFormat,
                formatting,
            } = input;
            const parameterMeta: RevaSpecInputParameter = {
                parameter,
                required,
                type,
                minValue,
                maxValue,
                nameDe,
                nameEn,
                descriptionDe,
                descriptionEn,
                guiAdditionalHelpTextDe,
                guiAdditionalHelpTextEn,
                unitDe,
                unitEn,
                categories: convertParamCategories(categories),
                displayFormat,
                formatting,
                parameterType,
            };
            return [parameter, parameterMeta];
        });
        return [segment, Object.fromEntries(inputMeta)];
    });
    return Object.fromEntries(inputsBySegmentArr);
}

/**
 * Convert one AVM segment's output parameters to internal format, tagging every parameter with the
 * given `parameterType`.
 */
function convertOutputParams(
    outputJson: AvmSpecOutputParameter[] | undefined,
    parameterType: RevaValueParameterType,
): Record<string, RevaSpecOutputParameter> {
    if (!Array.isArray(outputJson)) {
        console.warn("Invalid output spec?", outputJson);
        outputJson = [];
    }
    const outputMeta = outputJson.map((output) => {
        const {parameter, type, nameDe, nameEn, descriptionDe, descriptionEn, unitDe, unitEn, categories} = output;
        const parameterMeta: RevaSpecOutputParameter = {
            parameter,
            type,
            nameDe,
            nameEn,
            descriptionDe,
            descriptionEn,
            unitDe,
            unitEn,
            categories: convertParamCategories(categories),
            parameterType,
        };
        return [parameter, parameterMeta];
    });
    return Object.fromEntries(outputMeta);
}

/** Runtime shape of `avm.*`: dynamic string-key lookup by `SpecConversionConfig`'s segment keys. */
type AvmData = Partial<Record<string, AvmSpec | AvmSpecOutputOnly>>;

function convertApiParams(avmData: AvmData, config: SpecConversionConfig): RevaSpecApiParams {
    const apiParams: Record<string, RevaSpecSegmentInputs> = {};
    for (const {key, parameterType} of config.inputSegments) {
        apiParams[key] = convertSegmentInputs(avmData?.[key], parameterType);
    }
    const output: Record<string, RevaSpecOutputParameter> = {};
    for (const {key, parameterType} of config.outputSegments) {
        Object.assign(output, convertOutputParams(avmData?.[key]?.outputParameters, parameterType));
    }
    return {...apiParams, output} as RevaSpecApiParams;
}

/**
 * Convert the raw spec API response into the internal {@link RevaSpec} format.
 */
export function convertRevaSpecResponse(
    apiResponse: RevaSpecApiResponse | null | undefined,
    config: SpecConversionConfig,
): RevaSpec {
    const revaData: Partial<RevaSpecApiResponse["reva"]> = apiResponse?.reva ?? {};
    const avmData: Partial<RevaSpecApiResponse["avm"]> = apiResponse?.avm ?? {};
    // Ertrags- und Sachwert haben keine eigene AVM-Spec, ihre Parameter liegen in der von VALUE
    const valueFormats = indexDisplayFormats(avmData.VALUE);
    return {
        pages: {
            VALUE: convertPage(revaData.VALUE, valueFormats),
            COMPARATIVE_VALUE: convertPage(revaData.COMPARATIVE_VALUE, indexDisplayFormats(avmData.COMPARATIVE_VALUE)),
            INCOME_VALUE: convertPage(revaData.INCOME_VALUE, valueFormats),
            REAL_ASSET_VALUE: convertPage(revaData.REAL_ASSET_VALUE, valueFormats),
            ENERGY_VALUE: convertPage(revaData.ENERGY_VALUE, indexDisplayFormats(avmData.ENERGY_VALUE)),
        },
        objectInputs: revaData.input ?? [],
        apiParams: convertApiParams(avmData as AvmData, config),
        avmSpec: {
            ENERGY_VALUE: avmData.ENERGY_VALUE,
            COMPARATIVE_VALUE: avmData.COMPARATIVE_VALUE,
            QUALITY_FURNISHINGS: avmData.QUALITY_FURNISHINGS,
            LOCATION_INFORMATION_PRICE_INDEX: avmData.LOCATION_INFORMATION_PRICE_INDEX,
            LOCATION_INFORMATION_STANDARD_GROUND_VALUE: avmData.LOCATION_INFORMATION_STANDARD_GROUND_VALUE,
            LOCATION_INFORMATION_CLIMATE_RISK: avmData.LOCATION_INFORMATION_CLIMATE_RISK,
            LOCATION_INFORMATION_DESCRIPTION: avmData.LOCATION_INFORMATION_DESCRIPTION,
        },
    };
}

// ---------------------------------------------------------------------------
// Spec lookup helpers
// ---------------------------------------------------------------------------

/**
 * Find a named output parameter row in a spec page (by its `market_value.field`).
 */
export function findPageOutputParam(
    spec: RevaSpec | null | undefined,
    page: RevaIndicationEndpoint | string,
    paramName: string,
): RevaSpecOutputRow | undefined {
    return spec?.pages?.[page as RevaIndicationEndpoint]?.rows?.find((row) => row?.market_value?.field === paramName);
}

/**
 * Find a named output parameter of an AVM spec (any endpoint).
 */
export function findOutputParam(
    spec: AvmSpec | AvmSpecOutputOnly | null | undefined,
    paramName: string,
): AvmSpecOutputParameter | undefined {
    return spec?.outputParameters?.find?.((p) => p.parameter === paramName);
}

/**
 * Maps every "reva.input" parameter with select-button ranges (#18858) to its ranges, across all
 * object-input pages - so a caller with only a parameter key can still look up its ranges,
 */
export function indexRangesByApiParam(spec: RevaSpec | null | undefined): Record<string, RevaSpecObjectInputRange[]> {
    return (spec?.objectInputs || [])
        .flatMap((p) => p.columns)
        .flatMap((c) => c.blocks)
        .flatMap((b) => b.rows)
        .filter((r) => r.ranges?.length)
        .reduce<Record<string, RevaSpecObjectInputRange[]>>((acc, r) => {
            acc[r.parameter] = r.ranges as RevaSpecObjectInputRange[];
            return acc;
        }, {});
}

/**
 * Create an indexed map (JS object) containing all properties of a model of a parameter or model property.
 * @param param either an AVM parameter/property object, or a model object, or the property array itself
 * @return aggregated map, keyed by property name
 */
export function indexModelProperties(
    param:
        | AvmSpecOutputModelParameter[]
        | AvmSpecOutputModel
        | AvmSpecOutputParameter
        | AvmSpecOutputModelParameter
        | null
        | undefined,
): Record<string, AvmSpecOutputModelParameter> {
    let target: AvmSpecOutputModelParameter[] | undefined;
    if (Array.isArray(param)) {
        target = param;
    } else if (param && "properties" in param && Array.isArray(param.properties)) {
        target = param.properties;
    } else if (param && "model" in param && Array.isArray(param.model?.properties)) {
        target = param.model?.properties;
    }
    if (!target) {
        return {};
    }
    return target.reduce<Record<string, AvmSpecOutputModelParameter>>((acc, prop) => {
        acc[prop.property] = {...prop};
        return acc;
    }, {});
}
